import { pantryColors } from '@pantry/ui'
import type {
  CannabinoidEntry,
  PageScoreAreaId,
  PageScoreState,
  ProductImage,
  VariationGroupItem,
  VariationImagePatch,
} from '../../types/product'
import { effectiveVariationImages, PUBLISHED_VARIATION_STATUS } from './utils/variationImages'

// Page Score — client-side page-completeness score (0–100) shown in the product
// editor. Six equally weighted areas; see ./docs/page-score.md for the rules.
// Pure functions only so the doc, the editor sidebar and the SEO section all
// read from one source.

// DOM ids the editor assigns to each scored region so Page Score / SEO rows can
// scroll to (and expand) them.
export const PAGE_SCORE_TARGET = {
  brand: 'ps-brand',
  category: 'ps-category',
  gallery: 'ps-gallery',
  productName: 'ps-product-name',
  description: 'ps-description',
  specs: 'ps-specs',
  seo: 'ps-seo',
  seoKeyword: 'ps-seo-keyword',
  seoMeta: 'ps-seo-meta',
} as const

export type PageScoreTarget = (typeof PAGE_SCORE_TARGET)[keyof typeof PAGE_SCORE_TARGET]

export type SeoCheckId =
  | 'keyword_set'
  | 'keyword_in_name'
  | 'keyword_in_description'
  | 'meta_present'
  | 'meta_has_keyword'
  | 'internal_link'
  | 'image_alt'

export interface SeoCheck {
  id: SeoCheckId
  ok: boolean
  label: string
  help: string
  target: PageScoreTarget
}

export type { PageScoreAreaId }

export interface PageScoreArea {
  id: PageScoreAreaId
  name: string
  // Completion fraction in [0, 1].
  value: number
  // Status copy shown when the area is fully complete.
  goodLabel: string
  target: PageScoreTarget
  // Customer-facing list of failing sub-checks (Specifications / SEO only),
  // shown in the row tooltip. Empty for single yes/no areas.
  issues: string[]
}

export interface PageScoreInput {
  productName: string
  brand: string
  categoryIds: string[]
  images: Pick<ProductImage, 'alt'>[]
  longDescription: string
  cannabinoids: CannabinoidEntry[]
  seoKeyphrase: string
  seoDescription: string
  storeUrl: string
  // True when at least one published variation has an image. Counts toward the
  // Primary image area.
  hasVariationImage?: boolean
  // Images scored by the SEO alt-text check: parent images plus published
  // variation images (see collectSeoImages). Falls back to `images` when absent.
  altImages?: Pick<ProductImage, 'alt'>[]
}

export interface PageScoreResult {
  score: number
  areas: PageScoreArea[]
  areasToImprove: number
  // All SEO checks — feed the SEO area and SEO ring.
  seoChecks: SeoCheck[]
  seoPassed: number
  seoScore: number
}

export const SEO_CHECK_COUNT = 7
export const SPEC_CHECK_COUNT = 2

// Every run of characters that are not Unicode letters or numbers. Built with
// the RegExp constructor because the lib's TS target (es5) rejects a `u`-flag
// regex literal.
const NON_WORD_RUN = new RegExp('[^\\p{L}\\p{N}]+', 'gu')

// Lowercase and collapse every run of non letter/number characters (any
// script) to a single space, so "OG-Kush 3.5g" and "og kush 3 5g" compare equal.
export function normalizeKeyword(text: string): string {
  return text.toLowerCase().replace(NON_WORD_RUN, ' ').trim()
}

// Whole-phrase match on normalized text ("kush" does not match "kushy").
export function containsKeyword(text: string, normalizedKeyword: string): boolean {
  if (!normalizedKeyword) return false
  return ` ${normalizeKeyword(text)} `.includes(` ${normalizedKeyword} `)
}

function htmlToText(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function originOf(storeUrl: string): string | null {
  if (!storeUrl) return null
  try {
    return new URL(/^https?:\/\//i.test(storeUrl) ? storeUrl : `https://${storeUrl}`).origin
  } catch {
    return null
  }
}

// Internal link = <a href> with visible text, not a #fragment, pointing at the
// store (relative, or absolute on the store origin), and not the bare homepage.
export function countInternalLinks(html: string, storeUrl: string): number {
  const storeOrigin = originOf(storeUrl)
  const base = storeOrigin ?? 'https://store.invalid'
  const re = /<a\b[^>]*\bhref\s*=\s*(["'])(.*?)\1[^>]*>([\s\S]*?)<\/a>/gi
  let count = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(html)) !== null) {
    const raw = m[2].trim()
    const text = htmlToText(m[3])
    if (!raw || !text || raw.startsWith('#')) continue
    let url: URL
    try {
      url = new URL(raw, base)
    } catch {
      continue
    }
    if (url.origin !== base) continue
    if (url.pathname === '/' || url.pathname === '') continue
    count += 1
  }
  return count
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

export function computeSeoChecks(input: PageScoreInput): SeoCheck[] {
  const keyword = normalizeKeyword(input.seoKeyphrase)
  const body = htmlToText(input.longDescription)
  const meta = input.seoDescription.trim()
  const links = countInternalLinks(input.longDescription, input.storeUrl)
  const altImages = input.altImages ?? input.images
  // The storefront backfills an empty alt with the product name.
  const missingAlt = altImages.filter((img) => !img.alt?.trim() && !input.productName.trim()).length
  const inName = containsKeyword(input.productName, keyword)
  const inBody = containsKeyword(body, keyword)
  const inMeta = containsKeyword(meta, keyword)

  let altLabel: string
  if (!altImages.length) altLabel = 'Add an image, then its alt text'
  else if (missingAlt) altLabel = `Alt text is missing on ${plural(missingAlt, 'image')}`
  else altLabel = 'All product images have alt text'

  return [
    {
      id: 'keyword_set',
      ok: !!keyword,
      label: keyword ? 'Focus Keyword is set' : 'Set a Focus Keyword',
      help: 'Choose one phrase for this product.',
      target: PAGE_SCORE_TARGET.seoKeyword,
    },
    {
      id: 'keyword_in_name',
      ok: inName,
      label: `Focus Keyword ${inName ? 'appears in' : 'is missing from'} product name`,
      help: 'Keep the product name accurate and natural.',
      target: PAGE_SCORE_TARGET.productName,
    },
    {
      id: 'keyword_in_description',
      ok: inBody,
      label: `Focus Keyword ${inBody ? 'is used in' : 'is missing from'} product description`,
      help: 'Mention it naturally where it helps the reader.',
      target: PAGE_SCORE_TARGET.description,
    },
    {
      id: 'meta_present',
      ok: !!meta,
      label: `Meta Description is ${meta ? 'present' : 'missing'}`,
      help: 'Summarize the product for the search preview.',
      target: PAGE_SCORE_TARGET.seoMeta,
    },
    {
      id: 'meta_has_keyword',
      ok: inMeta,
      label: `Meta Description ${inMeta ? 'includes' : 'is missing'} Focus Keyword`,
      help: 'Include the phrase naturally in your summary.',
      target: PAGE_SCORE_TARGET.seoMeta,
    },
    {
      id: 'internal_link',
      ok: links > 0,
      label: links ? `${plural(links, 'internal link')} found` : 'Add a relevant internal link',
      help: 'Link to a related product or category; review relevance.',
      target: PAGE_SCORE_TARGET.description,
    },
    {
      id: 'image_alt',
      ok: altImages.length > 0 && missingAlt === 0,
      label: altLabel,
      help: 'Describe each meaningful product image.',
      target: PAGE_SCORE_TARGET.gallery,
    },
  ]
}

// True when any published variation has an image, after applying the editor's
// unsaved per-variation image edits: a `null` override means the image was
// removed; an object override is merged over `item.image` (same as the gallery
// in ProductInfoSection; see effectiveVariationImages).
export function anyPublishedVariationHasImage(
  items: Pick<VariationGroupItem, '_id' | 'status' | 'image' | 'images'>[],
  overrides?: Record<string, VariationImagePatch | null>,
): boolean {
  return items.some(
    (item) => item.status === PUBLISHED_VARIATION_STATUS && effectiveVariationImages(item, overrides).length > 0,
  )
}

// A cannabinoid range passes when both ends are set, min >= 0 and max >= min.
// Zero is a valid value.
function hasValidRange(cannabinoids: CannabinoidEntry[], name: CannabinoidEntry['name']): boolean {
  const entry = cannabinoids.find((c) => c.name === name)
  const min = entry?.min
  const max = entry?.max
  if (min == null || max == null) return false
  if (Number.isNaN(Number(min)) || Number.isNaN(Number(max))) return false
  return Number(min) >= 0 && Number(max) >= Number(min)
}

// Net weight and cannabis equivalent are POS-provided (read-only) and are not
// scored.
export interface SpecCheck {
  ok: boolean
  label: string
}

export function computeSpecChecks(input: Pick<PageScoreInput, 'cannabinoids'>): SpecCheck[] {
  return [
    { ok: hasValidRange(input.cannabinoids, 'THC'), label: 'THC range: enter min and max (max ≥ min)' },
    { ok: hasValidRange(input.cannabinoids, 'CBD'), label: 'CBD range: enter min and max (max ≥ min)' },
  ]
}

export function computePageScore(input: PageScoreInput): PageScoreResult {
  const seoChecks = computeSeoChecks(input)
  const seoPassed = seoChecks.filter((c) => c.ok).length
  const specChecks = computeSpecChecks(input)
  const specPassed = specChecks.filter((c) => c.ok).length
  const failing = (checks: { ok: boolean; label: string }[]) => checks.filter((c) => !c.ok).map((c) => c.label)

  const areas: PageScoreArea[] = [
    { id: 'brand', name: 'Brand', value: input.brand ? 1 : 0, goodLabel: 'Assigned', target: PAGE_SCORE_TARGET.brand, issues: [] },
    { id: 'category', name: 'Category', value: input.categoryIds.length ? 1 : 0, goodLabel: 'Assigned', target: PAGE_SCORE_TARGET.category, issues: [] },
    { id: 'image', name: 'Primary image', value: input.images.length || input.hasVariationImage ? 1 : 0, goodLabel: 'Present', target: PAGE_SCORE_TARGET.gallery, issues: [] },
    { id: 'description', name: 'Product description', value: htmlToText(input.longDescription) ? 1 : 0, goodLabel: 'Present', target: PAGE_SCORE_TARGET.description, issues: [] },
    { id: 'specs', name: 'Specifications', value: specPassed / SPEC_CHECK_COUNT, goodLabel: 'Complete', target: PAGE_SCORE_TARGET.specs, issues: failing(specChecks) },
    { id: 'seo', name: 'SEO', value: seoPassed / SEO_CHECK_COUNT, goodLabel: 'Complete', target: PAGE_SCORE_TARGET.seo, issues: failing(seoChecks) },
  ]

  const sum = areas.reduce((acc, a) => acc + a.value, 0)
  return {
    score: Math.round((sum / areas.length) * 100),
    areas,
    areasToImprove: areas.filter((a) => a.value < 1).length,
    seoChecks,
    seoPassed,
    seoScore: Math.round((seoPassed / SEO_CHECK_COUNT) * 100),
  }
}

// Scroll to a scored region, expanding its accordion first when collapsed, then
// briefly outline it and focus its first editable control.
export function jumpToPageScoreTarget(target: PageScoreTarget): void {
  const el = document.getElementById(target)
  if (!el) return
  const accordion = el.closest('[data-accordion-card]')
  const header = accordion?.querySelector<HTMLElement>('[data-accordion-header][aria-expanded="false"]')
  if (header) header.click()

  const run = () => {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const prevOutline = el.style.outline
    const prevOffset = el.style.outlineOffset
    el.style.outline = `3px solid ${pantryColors.blue[200]}`
    el.style.outlineOffset = '4px'
    window.setTimeout(() => {
      el.style.outline = prevOutline
      el.style.outlineOffset = prevOffset
    }, 1800)
    const focusable = el.matches('input, textarea, [contenteditable="true"]')
      ? el
      : el.querySelector<HTMLElement>('input:not([readonly]), textarea, [contenteditable="true"]')
    focusable?.focus({ preventScroll: true })
  }
  // Wait for the accordion's max-height transition (.3s) before scrolling.
  if (header) window.setTimeout(run, 320)
  else run()
}

// DOM id of the Variations card, and the event VariationsSection listens to in
// order to expand + flash the rows with custom specifications (ACD-15223).
export const VARIATIONS_CARD_ID = 'ps-variations'
export const REVEAL_CUSTOM_VARIANTS_EVENT = 'pantry:reveal-custom-variants'

// "N of M variants customized" badge: expand the Variations card, ask it to
// open + flash the customized rows, then scroll it into view.
export function revealCustomVariants(): void {
  const card = document.getElementById(VARIATIONS_CARD_ID)
  if (!card) return
  const header = card.querySelector<HTMLElement>(':scope > [data-accordion-header][aria-expanded="false"]')
  if (header) header.click()
  window.dispatchEvent(new CustomEvent(REVEAL_CUSTOM_VARIANTS_EVENT))
  const scroll = () => card.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  // Wait for the accordion's max-height transition (.3s) before scrolling.
  if (header) window.setTimeout(scroll, 320)
  else scroll()
}

// ── List bands / filter states ──────────────────────────────────────────────
// 3 colour bands for the backend list score: >=75 good, 45-74 warn, <45 bad.
// Same thresholds as the backend `page_score_states` filter (warn == 'weak').
export type PageScoreBand = 'good' | 'warn' | 'bad'

export const pageScoreBand = (score: number): PageScoreBand => {
  if (score >= 75) return 'good'
  if (score >= 45) return 'warn'
  return 'bad'
}

export const BAND_FILL: Record<PageScoreBand, string> = {
  good: pantryColors.brand.primary,
  warn: pantryColors.amber[700],
  bad: pantryColors.red[700],
}

export interface PageScoreStateOption {
  value: PageScoreState
  label: string
  // Score range shown next to the label.
  hint: string
  color: string
}

// Options for the list's Page Score filter, in canonical order.
export const PAGE_SCORE_STATE_OPTIONS: PageScoreStateOption[] = [
  { value: 'good', label: 'Good', hint: '75–100', color: BAND_FILL.good },
  { value: 'weak', label: 'Weak', hint: '45–74', color: BAND_FILL.warn },
  { value: 'bad', label: 'Bad', hint: '0–44', color: BAND_FILL.bad },
]

// Options for the list's Page Score "Needs Improvement" (area) filter.
export const PAGE_SCORE_AREA_OPTIONS: { value: PageScoreAreaId; label: string }[] = [
  { value: 'brand', label: 'Brand' },
  { value: 'category', label: 'Category' },
  { value: 'image', label: 'Image' },
  { value: 'description', label: 'Description' },
  { value: 'specs', label: 'Specifications' },
  { value: 'seo', label: 'SEO' },
]

export const PAGE_SCORE_STATE_LABEL = Object.fromEntries(
  PAGE_SCORE_STATE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<PageScoreState, string>

export const PAGE_SCORE_AREA_LABEL = Object.fromEntries(
  PAGE_SCORE_AREA_OPTIONS.map((o) => [o.value, o.label]),
) as Record<PageScoreAreaId, string>

// States applied by the "Needs Improvement" stat card (score < 75).
export const NEEDS_IMPROVEMENT_STATES: PageScoreState[] = ['weak', 'bad']
